import {useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {useForgotPasswordMutation} from '../Auth.api'

export function PassworForgotten() {
  const intl = useIntl()
  const navigate = useNavigate()
  
  const didSubmit = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isComplete, setIsComplete] = useState(false)
  const [forgotPassword] = useForgotPasswordMutation()

  const forgotPasswordSchema = Yup.object().shape({
    organization: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    email: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues: {
      organization: '',
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!didSubmit.current) {
        try {
          setSubmitting(true)
          const response: any = await forgotPassword({
            organization: values.organization,
            email: values.email,
          })

          if (response && response.error) {
            const {error} = response
            switch (error.status) {
              case 500:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
                break
              default:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
            }
          } else {
            setIsComplete(true)
          }
        } catch (e: any) {
          setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-row-auto'>
      {!isComplete && (
        <>
          <div className='d-flex flex-column-fluid flex-start'>
            <h1 className='text-white text-left mb-2'>
              {intl.formatMessage({id: 'AUTH.PASSWORD_FORGOTTEN.TITLE'})}
            </h1>
          </div>
          <div className='d-flex flex-column-fluid flex-left'>
            <div className='text-white text-left mb-5'>
              {intl.formatMessage({id: 'AUTH.PASSWORD_FORGOTTEN.DESCRIPTION'})}
            </div>
          </div>
        </>
      )}

      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column-fluid flex-center'>
          <div className='d-flex flex-column flex-row-fluid'>
            <div className='d-flex flex-column-auto flex-center'>
              {!formik.isSubmitting && error && (
                <div className='mb-5 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{error}</div>
                </div>
              )}
              {!formik.isSubmitting && !error && isComplete && (
                <div className='mb-5 bg-light-info p-8 rounded'>
                  <div className='text-info'>
                    {intl.formatMessage({id: 'AUTH.PASSWORD_FORGOTTEN.SUCCESS_MESSAGE'})}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isComplete ? (
          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => navigate(`/auth/password-recovery`)}
              >
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'NEXT'}).toUpperCase()}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className='d-flex flex-column-fluid flex-center'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-column-auto flex-center'>
                  <div className='form fv-row mb-5'>
                    <i className='text-white form-control-icon fa fa-building icon font-weight-bold'></i>
                    <input
                      placeholder={intl.formatMessage({id: 'ORGANIZATION'})}
                      {...formik.getFieldProps('organization')}
                      className={clsx(
                        'placeholder-gray-400 form-control form-control-auth bg-transparent',
                        {
                          'is-invalid': formik.touched.organization && formik.errors.organization,
                        },
                        {
                          'is-valid': formik.touched.organization && !formik.errors.organization,
                        }
                      )}
                      disabled={formik.isSubmitting}
                      type='text'
                      name='organization'
                      autoComplete='off'
                    />
                    {formik.touched.organization && formik.errors.organization && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.organization}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='d-flex flex-column-auto flex-center'>
                  <div className='form fv-row mb-5'>
                    <i className='text-white form-control-icon fa fa-user-circle icon font-weight-bold'></i>
                    <input
                      placeholder={intl.formatMessage({id: 'EMAIL'})}
                      {...formik.getFieldProps('email')}
                      className={clsx(
                        'placeholder-gray-400 form-control form-control-auth bg-transparent',
                        {
                          'is-invalid': formik.touched.email && formik.errors.email,
                        },
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                      disabled={formik.isSubmitting}
                      type='text'
                      name='email'
                      autoComplete='off'
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.email}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex flex-row mt-2'>
              <div className='d-flex flex-column flex-row-fluid'>
                <button
                  type='submit'
                  className='btn btn-sm btn-light'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!formik.isSubmitting && (
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'SUBMIT'}).toUpperCase()}
                    </span>
                  )}
                  {formik.isSubmitting && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </form>

      {!isComplete && (
        <div className='d-flex flex-row flex-start mt-2'>
          <span className='text-white me-2'>
            {intl.formatMessage({id: 'AUTH.SIGN_IN.BACK_TO'})}
          </span>{' '}
          <Link to='/auth' className='link-primary text-white fw-semibold '>
            <u>{intl.formatMessage({id: 'AUTH.SIGN_IN.TITLE'})}</u>
          </Link>
        </div>
      )}
    </div>
  )
}
